import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { PromptBuilderMockup } from "./PromptBuilderMockup";
import { ICONS } from "@/lib/icons";
import copy from "@/content/homepage-copy.json";

const { arrowRight: ArrowRight, check: Check } = ICONS;

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.85, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const h = copy.hero;
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -50]);
  const yPanel = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 45]);

  const titleLines = h.titleLines || [h.title];

  return (
    <section
      id="top"
      ref={ref}
      className="radial-fade relative overflow-hidden pt-[104px] pb-14 md:pt-[128px] md:pb-14"
      data-testid="hero-section"
    >
      <div className="mx-auto grid max-w-content grid-cols-1 items-center gap-12 px-4 md:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-[64px]">
        {/* left */}
        <motion.div style={{ y }}>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="mb-5 font-body text-[12px] font-semibold tracking-[0.16em] text-brand-accent"
            data-testid="hero-eyebrow"
          >
            {h.eyebrow}
          </motion.p>

          <h1
            className="max-w-[640px] font-display text-[38px] font-medium leading-[1.06] tracking-[-0.03em] md:text-[58px] md:leading-[1.03] md:tracking-[-0.035em]"
            data-testid="hero-title"
          >
            {titleLines.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span
                  className="block"
                  custom={i}
                  variants={lineReveal}
                  initial={reduce ? "show" : "hidden"}
                  animate="show"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="mt-6 max-w-[600px] text-[19px] leading-[1.55] text-brand-sec md:text-[20px]"
            data-testid="hero-lead"
          >
            {h.lead}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.62, duration: 0.6 }}
            className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <a
              href="/asystent"
              data-testid="hero-primary-cta"
              className="group inline-flex h-14 items-center justify-center gap-2 rounded-field bg-brand-accent px-7 text-base font-semibold text-brand-on transition-transform duration-200 hover:-translate-y-0.5"
            >
              {h.primaryCta}
              <ArrowRight size={18} strokeWidth={2} className="transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
            </a>
            <a
              href="#pakiety"
              data-testid="hero-secondary-cta"
              className="inline-flex h-14 items-center justify-center gap-2 rounded-field border border-brand-line px-6 text-base font-semibold text-brand-text transition-colors duration-200 hover:border-brand-accent/60"
            >
              {h.secondaryCta}
            </a>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="mt-8 flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:gap-x-5 sm:gap-y-2"
            data-testid="hero-benefits"
          >
            {h.benefits.map((b) => (
              <li key={b} className="flex items-center gap-2 text-[13px] text-brand-sec">
                <Check size={16} strokeWidth={1.75} className="shrink-0 text-brand-accent" aria-hidden="true" />
                {b}
              </li>
            ))}
          </motion.ul>
        </motion.div>

        {/* right */}
        <motion.div
          style={{ y: yPanel }}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.35, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="w-full"
        >
          <PromptBuilderMockup />
        </motion.div>
      </div>
    </section>
  );
};
